import React, { useState, useMemo, useCallback } from 'react';
import Table from '../../components/Table/Table';
import CarForm from '../../components/HR/CarForm';
import { Car, TableColumn } from '../../types';
import { useBranches } from '../../hooks/useBranches';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { bumpCodePrefixSequence } from '../../utils/codePrefixUtils';
import { useCars, useCreateCar, useUpdateCar, useDeleteCar } from '../../hooks/useCars';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';

const Cars: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingCar, setEditingCar] = useState<Car | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [yearFilter, setYearFilter] = useState<string>('all');
  const [expiryDateFilter, setExpiryDateFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');

  const queryOptions = useMemo(() => ({
    pagination: { page: 1, pageSize: 1000 },
    sort: [{ field: 'code', order: 'asc' as const }]
  }), []);

  const { data: cars, isLoading, error, refetch } = useCars(queryOptions);
  const { data: branches = [] } = useBranches(queryOptions);

  const createMutation = useCreateCar(() => refetch());
  const updateMutation = useUpdateCar(() => refetch());
  const deleteMutation = useDeleteCar(() => refetch());

  const getBranchName = useCallback((branchId: string) => {
    const branch = branches.find(b => b.id === branchId);
    return branch ? branch.name : '-';
  }, [branches]);

  const uniqueYears = useMemo(() => {
    if (!cars || cars.length === 0) return [];
    return Array.from(new Set(cars.map(car => car.manufacturingYear).filter(Boolean)));
  }, [cars]);

  const isExpiringSoon = (expiryDate: string) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  };

  const isExpired = (expiryDate: string) => new Date(expiryDate) < new Date();

  const filteredCars = useMemo(() => {
    if (!cars || cars.length === 0) return [];
    return cars.filter(car => {
      const branchName = getBranchName(car.branchId);
      const matchesSearch = !searchTerm || (
        car.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        car.ownerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        car.plateNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        car.chassisNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        car.engineNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branchName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesStatus = statusFilter === 'all' || car.status === statusFilter;
      const matchesBranch = branchFilter === 'all' || car.branchId === branchFilter;
      const matchesYear = yearFilter === 'all' || car.manufacturingYear === yearFilter;
      const matchesExpiryDate = expiryDateFilter === 'all' ||
        (expiryDateFilter === 'expiring_soon' && isExpiringSoon(car.licenseExpiry)) ||
        (expiryDateFilter === 'expired' && isExpired(car.licenseExpiry)) ||
        (expiryDateFilter === 'valid' && !isExpired(car.licenseExpiry) && !isExpiringSoon(car.licenseExpiry));
      const expiryDate = new Date(car.licenseExpiry);
      const matchesDateFrom = !dateFromFilter || expiryDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || expiryDate <= new Date(dateToFilter);
      return matchesSearch && matchesStatus && matchesBranch && matchesYear &&
        matchesExpiryDate && matchesDateFrom && matchesDateTo;
    });
  }, [cars, searchTerm, statusFilter, branchFilter, yearFilter, expiryDateFilter, dateFromFilter, dateToFilter, getBranchName]);

  const handleExport = () => {
    const exportData = filteredCars.map(car => ({
      Code: car.code,
      'Owner Name': car.ownerName,
      'Plate Number': car.plateNumber,
      'Chassis Number': car.chassisNumber,
      'Engine Number': car.engineNumber,
      'Manufacturing Year': car.manufacturingYear,
      'License Expiry': car.licenseExpiry,
      Status: car.status,
      'Vessel/Headquarters': getBranchName(car.branchId),
      'Document Count': car.documents?.length || 0,
      'Created At': car.createdAt,
      'Updated At': car.updatedAt
    }));
    exportToExcel(exportData, `cars_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = filteredCars.map(car => ({
      Code: car.code,
      'Owner Name': car.ownerName,
      'Plate Number': car.plateNumber,
      'Chassis Number': car.chassisNumber,
      'Engine Number': car.engineNumber,
      'Manufacturing Year': car.manufacturingYear,
      'License Expiry': car.licenseExpiry,
      Status: car.status,
      'Vessel/Headquarters': getBranchName(car.branchId),
      'Document Count': car.documents?.length || 0,
      'Created At': car.createdAt,
      'Updated At': car.updatedAt
    }));
    printAsExcel(exportData, 'Cars');
  };

  const filters: FilterConfig[] = [
    { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: setStatusFilter, options: [
      { value: 'all', label: 'All Statuses' }, { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }, { value: 'maintenance', label: 'Maintenance' }
    ]},
    { key: 'branch', label: 'Vessel/Headquarters', type: 'select', value: branchFilter, onChange: setBranchFilter, options: [
      { value: 'all', label: 'All Vessels/Headquarters' }, ...branches.map(b => ({ value: b.id, label: b.name }))
    ]},
    { key: 'year', label: 'Manufacturing Year', type: 'select', value: yearFilter, onChange: setYearFilter, options: [
      { value: 'all', label: 'All Years' }, ...uniqueYears.map(y => ({ value: y, label: y }))
    ]},
    { key: 'expiry', label: 'License Status', type: 'select', value: expiryDateFilter, onChange: setExpiryDateFilter, options: [
      { value: 'all', label: 'All' }, { value: 'valid', label: 'Valid' }, { value: 'expiring_soon', label: 'Expiring Soon' }, { value: 'expired', label: 'Expired' }
    ]},
    { key: 'dateFrom', label: 'Expiry From', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Expiry To', type: 'date', value: dateToFilter, onChange: setDateToFilter },
  ];

  const columns: TableColumn[] = [
    { key: 'code', label: 'الكود', sortable: true },
    { key: 'ownerName', label: 'اسم المالك', sortable: true },
    { key: 'plateNumber', label: 'رقم السيارة', sortable: true },
    { key: 'chassisNumber', label: 'رقم الشاسيه', sortable: true },
    { key: 'engineNumber', label: 'رقم الموتور', sortable: true },
    { key: 'manufacturingYear', label: 'سنة الصنع', sortable: true },
    { key: 'licenseExpiry', label: 'تاريخ انتهاء الرخصة', sortable: true, render: (value: string) => new Date(value).toLocaleDateString('ar-EG') }
  ];

  const handleEdit = (car: Car) => {
    setEditingCar(car);
    setShowForm(true);
  };

  const handleDelete = async (car: Car) => {
    if (window.confirm(`Are you sure you want to delete car ${car.plateNumber}?`)) {
      await deleteMutation.execute(car);
    }
  };

  const handleAdd = () => {
    setEditingCar(undefined);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: Partial<Car>) => {
    if (editingCar) {
      const updateData = { ...data, documentId: editingCar.documentId };
      await updateMutation.execute({ id: editingCar.id, data: updateData });
    } else {
      await createMutation.execute(data);
      await bumpCodePrefixSequence(data.code);
    }
    setShowForm(false);
    setEditingCar(undefined);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingCar(undefined);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setBranchFilter('all');
    setYearFilter('all');
    setExpiryDateFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <CarForm car={editingCar} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
      </div>
    );
  }

  if (isLoading) return <TableSkeleton />;
  if (error) return <ErrorState message={error.message || String(error)} onRetry={() => refetch()} entityName="cars" />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cars Management"
        description="Manage company cars, their documents, and permits"
      />
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search cars..."
        filters={filters}
        onClearFilters={handleClearFilters}
        totalCount={cars?.length || 0}
        filteredCount={filteredCars.length}
        entityName="cars"
        onAdd={handleAdd}
        addLabel="Add Car"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <div className="card">
        <Table
          columns={columns}
          data={filteredCars}
          onEdit={handleEdit}
          onDelete={handleDelete}
          title="Cars Directory"
          showAddButton={false}
          searchable={false}
        />
      </div>
    </div>
  );
};

export default Cars;
